import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../api";
import { useContent } from "../../context/ContentContext";
import ImageUpload from "../../components/admin/ImageUpload";
import CategoryManager from "../../components/admin/CategoryManager";
import { IMAGE_SPECS } from "../../constants/imageSpecs";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { useToast } from "../../hooks/use-toast";
import { Plus, Pencil, Trash2, Search, Loader2, Tags } from "lucide-react";

const DOSAGE = "__dosage__"; // select value for dosage-form entries (Tablets, Capsules, …)
const EMPTY = { name: "", category: "", blurb: "", image: "" };

// What to show for a product's category
const categoryLabel = (p) => (p.group === "Dosage Forms" ? "Dosage form" : p.category || p.group);

export default function AdminProducts() {
  const { toast } = useToast();
  const { content, refresh } = useContent();
  const categories = content.productCategories || [];
  const [manageOpen, setManageOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/products");
      setProducts(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => products.filter((p) => p.name.toLowerCase().includes(query.toLowerCase())),
    [products, query]
  );

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    const cat = p.group === "Dosage Forms" ? DOSAGE : (p.category && p.category !== "Product Categories" ? p.category : "");
    setForm({ name: p.name, category: cat, blurb: p.blurb || "", image: p.image || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.category) {
      toast({ title: "Choose a category", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name, blurb: form.blurb, image: form.image,
      ...(form.category === DOSAGE
        ? { group: "Dosage Forms", category: "Dosage Forms" }
        : { group: "Product Categories", category: form.category }),
    };
    try {
      if (editing) {
        await adminApi.put(`/admin/products/${editing.id}`, payload);
        toast({ title: "Product updated" });
      } else {
        await adminApi.post("/admin/products", payload);
        toast({ title: "Product created" });
      }
      setOpen(false);
      await load();
      refresh();
    } catch (err) {
      toast({ title: "Save failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await adminApi.delete(`/admin/products/${deleteId}`);
      toast({ title: "Product deleted" });
      setDeleteId(null);
      await load();
      refresh();
    } catch (err) {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-[#0f1e33]">Products</h1>
          <p className="text-[#5a6b7e] mt-1">{products.length} products in the directory.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setManageOpen(true)} className="h-11">
            <Tags className="h-4 w-4 mr-1.5" /> Manage categories
          </Button>
          <Button onClick={openCreate} className="bg-[#0e7490] hover:bg-[#0b5563] text-white h-11">
            <Plus className="h-4 w-4 mr-1.5" /> Add product
          </Button>
        </div>
      </div>

      <div className="mt-6 relative max-w-sm">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9aa8b5]" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products..." className="pl-10 h-11 bg-white" />
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-border overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#5a6b7e]"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading...</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#5a6b7e] border-b border-border bg-[#f7fafc]">
                <th className="px-5 py-3 font-medium">Product</th>
                <th className="px-5 py-3 font-medium hidden md:table-cell">Category</th>
                <th className="px-5 py-3 font-medium hidden lg:table-cell">Slug</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0 hover:bg-[#f7fafc]">
                  <td className="px-5 py-3 font-medium text-[#0f1e33]">{p.name}</td>
                  <td className="px-5 py-3 hidden md:table-cell">
                    <span className="text-xs bg-[#eaf4f7] text-[#0e7490] px-2 py-0.5 rounded-full">{categoryLabel(p)}</span>
                  </td>
                  <td className="px-5 py-3 hidden lg:table-cell text-[#9aa8b5]">{p.slug}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(p)} className="h-8 w-8 rounded-lg border border-border flex items-center justify-center text-[#0e7490] hover:bg-[#eaf4f7]">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => setDeleteId(p.id)} className="h-8 w-8 rounded-lg border border-border flex items-center justify-center text-red-500 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-10 text-center text-[#9aa8b5]">No products found.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <CategoryManager open={manageOpen} onOpenChange={setManageOpen} products={products} onChanged={load} />

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit product" : "Add product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="p-name">Name</Label>
              <Input id="p-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Product name" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Category</Label>
                <button type="button" onClick={() => setManageOpen(true)} className="text-xs text-[#0e7490] hover:underline">Manage categories</button>
              </div>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                  <SelectItem value={DOSAGE}>Dosage form (e.g. Tablets, Capsules)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-blurb">Description</Label>
              <Textarea id="p-blurb" rows={3} value={form.blurb} onChange={(e) => setForm((f) => ({ ...f, blurb: e.target.value }))} placeholder="Short description" />
            </div>
            <ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} label="Product image" hint={IMAGE_SPECS.product.hint} minWidth={IMAGE_SPECS.product.minW} minHeight={IMAGE_SPECS.product.minH} />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#0e7490] hover:bg-[#0b5563] text-white">
                {saving ? "Saving..." : editing ? "Save changes" : "Create product"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">Delete product?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5a6b7e]">This action cannot be undone.</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
